import * as _ from 'lodash';
import React, { useState, useEffect } from 'react';
import useFetch from 'use-http';
import { useDispatch } from 'react-redux';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import PBNavBar from '../PBNavBar';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import CollapsibleSection from 'src/components/common/CollapsibleSection';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import TeNonReconSearch, { ITeNonReconSearchCriteria } from 'src/components/project-billing/components/TeNonReconSearch';
import EditTimeDialog, { IEditTimeDialogData } from 'src/components/project-billing/dialogs/EditTimeDialog';
import ChangeDetailDialog from 'src/components/project-billing/dialogs/ChangeDetailDialog';
import PayPeriod from 'src/models/AP/PayPeriod';
import TeNonReconProspect from 'src/components/project-billing/models/TeNonReconProspect';
import TeNonReconDetail from 'src/components/project-billing/models/TeNonReconDetail';
import TeNonReconChangeRecord from 'src/components/project-billing/models/TeNonReconChangeRecord';
import '../css/ProjectBilling.css';
import './TeNonRecon.css';

// Shared by every "don't wrap, but don't bleed into the next column either" cell (Project Name,
// Manager Name, Landman) - table-layout:fixed doesn't clip nowrap content on its own, so without
// the overflow/textOverflow pair a long value visually overruns the next column instead of eliding.
const NOWRAP_CLIP: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

// Ports te_nonrecon.fmt's Main canvas - a 4-panel accordion (TR block, T01-T04 titled; T05-T16 are
// unused placeholders per CanvasTitles, so they aren't reproduced). Panel 1 ("Pay Period", C01) is
// replaced by TeNonReconSearch (the same main-bar-plus-Advanced-Search pattern the Time & Expense
// Checklist page uses) rather than being its own collapsible section - its only job was holding the
// query fields, which the search component already owns. Panels 2-4 (C02/C03/C04) are
// CollapsibleSections below it, matching InvoiceGroupingDialog's own stacked-panel pattern.
const TeNonReconPage = () => {
    const dispatch = useDispatch();
    const { get, post, response } = useFetch();
    const { get: getDetail, response: detailResponse } = useFetch();
    const { get: getEditInfo, put: putEditInfo, response: editResponse } = useFetch();
    const { get: getExcel, response: excelResponse } = useFetch();

    const [payPeriods, setPayPeriods] = useState<Array<PayPeriod>>(undefined);
    const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>(undefined);
    const [payPeriodSearchOptions, setPayPeriodSearchOptions] = useState<ODataOptions>(undefined);
    const [payPeriodSearchTerm, setPayPeriodSearchTerm] = useState<string>('');
    // Mirrors B_EXPAND/B_COLL (ExpandAllCanvas/CollapseAllCanvas) - same single-boolean-passed-to-
    // every-CollapsibleSection's-forceExpanded pattern ProjectBillingPage.tsx already uses.
    const [expandAllSections, setExpandAllSections] = useState<boolean>(true);

    // QUERY_BILLING's own search criteria - Office lives in the main bar, everything else in
    // Advanced Search (see TeNonReconSearch).
    const [searchCriteria, setSearchCriteria] = useState<Omit<ITeNonReconSearchCriteria, 'periodNo'>>({});

    const [loadingProspects, setLoadingProspects] = useState<boolean>(false);
    const [prospects, setProspects] = useState<Array<TeNonReconProspect>>([]);

    // Mirrors B_BUILD - rebuilds TIME_SHEET_NORECON for the current period/office via
    // Obs.Remove_InvalidBilling + Obs.Build_Billing/Obs_Expanded.Build_Billing, then re-runs
    // QUERY_BILLING the same way Oracle's own Execute_Trigger('Query_Billing') does at the end.
    const [building, setBuilding] = useState<boolean>(false);
    const [exportingExcel, setExportingExcel] = useState<boolean>(false);

    // "Prospect Information" (C03) is detail-of whichever prospect row is selected above - mirrors
    // Oracle's own master-detail relationship, not a fresh QUERY_BILLING-style search.
    const [selectedProspect, setSelectedProspect] = useState<TeNonReconProspect | undefined>(undefined);
    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
    const [detailRows, setDetailRows] = useState<Array<TeNonReconDetail>>([]);

    // Backs the Edit/Reconcile popup (EDIT_TIME canvas, WHEN-MOUSE-DOUBLECLICK oid 285) - populated
    // right before showDialog so the dialog never renders with stale/empty data.
    const [editData, setEditData] = useState<IEditTimeDialogData | undefined>(undefined);

    const [loadingChangeRecords, setLoadingChangeRecords] = useState<boolean>(false);
    const [changeRecords, setChangeRecords] = useState<Array<TeNonReconChangeRecord>>([]);

    // Backs the change-history drill-down popup (CHANGE_DETAIL canvas, WHEN-MOUSE-DOUBLECLICK
    // oid 369) - the dialog fetches its own data off this tmId, same self-contained pattern
    // ArHistoryPaymentHistoryDialog.tsx uses.
    const [changeDetailTmId, setChangeDetailTmId] = useState<number | undefined>(undefined);

    const formatDateOrBlank = (value: Date | string | null | undefined): string => {
        if (!value) {
            return '';
        }
        const date = new Date(value);
        return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
    };

    // Same formatCurrency EmployeePayrollPage.tsx/TimeAndExpenseChecklistPage.tsx both use for their
    // own dollar columns.
    const formatCurrency = (value: number) =>
        (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

    const insertCharAt = (str: string, charToInsert: string, index: number) => str.slice(0, index) + charToInsert + str.slice(index);

    // Same pay-period fetch/format TimeAndExpenseChecklistPage.tsx uses ("YYYY-PP" display,
    // /v1.0/AccountsPayable/payperiods behind it).
    const getPayPeriods = async () => {
        const queryString = payPeriodSearchOptions.toString();
        let result = await get(`/v1.0/AccountsPayable/payperiods${queryString}`);

        if (response.ok) {
            setPayPeriods(result.items.map((pp: PayPeriod) => {
                const insertIndex = pp.periodNumber.toString().length - 2;
                const ppString = insertCharAt(pp.periodNumber.toString(), '-', insertIndex);
                return { id: pp.id, name: ppString, beginDate: pp.beginDate, endDate: pp.endDate };
            }));
        } else {
            console.log('Unable to get pay period data!!!');
        }
    };

    const initializePayPeriodSearchSettings = () => {
        const tableConfig: ITableConfig = {
            rowCount: 500,
            idField: 'Id',
            pageNumber: 1,
            columns: []
        };
        const newPayPeriodSearchOptions = ODataOptions.from(tableConfig);
        newPayPeriodSearchOptions.addOrderBy('Id', ODataOrderByDirection.Descending);
        const currentDate = new Date();
        const currentDate2 = new Date();
        const beginDate = new Date(currentDate.setMonth(currentDate.getMonth() - 12)).toLocaleDateString().replace('/', '-').replace('/', '-');
        const endDate = new Date(currentDate2.setMonth(currentDate2.getMonth() + 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
        newPayPeriodSearchOptions?.addFilter(new ODataFilter('end_date', ODataFilterOperators.LessThanOrEqual, endDate));
        newPayPeriodSearchOptions?.addFilter(new ODataFilter('beg_date', ODataFilterOperators.GreaterThanOrEqual, beginDate));
        setPayPeriodSearchOptions(newPayPeriodSearchOptions);
    };

    const resetPayPeriodSearchOptions = () => {
        let newPayPeriodSearchOptions = _.cloneDeep(payPeriodSearchOptions);
        newPayPeriodSearchOptions.clearFilters();
        newPayPeriodSearchOptions.addPaging(1, 200);
        const payPeriod = Number(payPeriodSearchTerm.replace('-', ''));
        if (!isNaN(payPeriod)) {
            newPayPeriodSearchOptions.addFilter(new ODataFilter('PERIOD_NO', ODataFilterOperators.Contains, payPeriod));
        }
        setPayPeriodSearchOptions(newPayPeriodSearchOptions);
    };

    useEffect(() => {
        initializePayPeriodSearchSettings();
    }, []);

    useEffect(() => {
        if (!!payPeriodSearchOptions) {
            getPayPeriods();
        }
    }, [payPeriodSearchOptions]);

    useEffect(() => {
        if ((!!payPeriodSearchTerm || payPeriodSearchTerm === '') && !!payPeriodSearchOptions) {
            resetPayPeriodSearchOptions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payPeriodSearchTerm]);

    // Mirrors QUERY_BILLING [oid 26] - runs both the Prospects (TIME_SHEET_NORECON_PROSPECTS) and
    // Modified Time Records (TIME_SHEET_CHANGE_RECORDS) queries together off one Search click, same
    // as Oracle does, and clears whatever detail-of-a-prospect selection was showing (a fresh search
    // can easily no longer include the previously-selected prospect).
    const runSearch = async (periodNo: number, criteria: Omit<ITeNonReconSearchCriteria, 'periodNo'>) => {
        setSelectedProspect(undefined);
        setDetailRows([]);

        setLoadingProspects(true);
        const query = new URLSearchParams();
        if (criteria.office) {
            query.set('office', criteria.office);
        }
        if (criteria.tsdMgr !== undefined) {
            query.set('tsdMgr', String(criteria.tsdMgr));
        }
        if (criteria.clNo !== undefined) {
            query.set('clNo', String(criteria.clNo));
        }
        if (criteria.prNo !== undefined) {
            query.set('prNo', String(criteria.prNo));
        }
        if (criteria.status) {
            query.set('status', criteria.status);
        }
        const queryString = query.toString();
        let result = await get(`/v1.0/AccountsPayable/timeandexpense/nonrecon/prospects/payperiod/${periodNo}${queryString ? `?${queryString}` : ''}`);
        setProspects(response.ok && Array.isArray(result) ? result : []);
        setLoadingProspects(false);

        setLoadingChangeRecords(true);
        const changeQuery = new URLSearchParams();
        if (criteria.clNo !== undefined) {
            changeQuery.set('clNo', String(criteria.clNo));
        }
        if (criteria.prNo !== undefined) {
            changeQuery.set('prNo', String(criteria.prNo));
        }
        const changeQueryString = changeQuery.toString();
        let changeResult = await get(`/v1.0/AccountsPayable/timeandexpense/nonrecon/changerecords/payperiod/${periodNo}${changeQueryString ? `?${changeQueryString}` : ''}`);
        setChangeRecords(response.ok && Array.isArray(changeResult) ? changeResult : []);
        setLoadingChangeRecords(false);
    };

    const handleSearch = (criteria: Omit<ITeNonReconSearchCriteria, 'periodNo'>) => {
        setSearchCriteria(criteria);
        if (!!selectedPayPeriod) {
            runSearch(Number(selectedPayPeriod), criteria);
        }
    };

    useEffect(() => {
        if (!!selectedPayPeriod) {
            runSearch(Number(selectedPayPeriod), searchCriteria);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPayPeriod]);

    // Mirrors B_BUILD's own guard - period_no is required, office must be a specific office (not
    // "*"/ALL, which only lives in the search criteria once Search has actually been run), and no
    // tsd_mgr filter can be set. Oracle's own fallback when this fails is Go_Block('OBS'), a screen
    // this app isn't porting, so an alert stands in for it here instead.
    //
    // Follows the same Generate-button pattern as ExcelReportDialog.tsx/ProjectBillingPage.tsx -
    // a .then() chain (not async/await) so the button's own loading spinner and disabled state are
    // driven purely by `building`, with an alert on completion either way.
    const handleBuild = async () => {
        if (!selectedPayPeriod) {
            window.alert('Please select a pay period before refreshing.');
            return;
        }
        if (!searchCriteria.office) {
            window.alert('Please search for a specific office before refreshing.');
            return;
        }
        if (searchCriteria.tsdMgr !== undefined) {
            window.alert('Refresh cannot be run with a TSDI Mgr No. filter set. Clear it and try again.');
            return;
        }

        const periodNo = Number(selectedPayPeriod);
        const office = searchCriteria.office;
        setBuilding(true);
        try {
            await post(`/v1.0/AccountsPayable/timeandexpense/nonrecon/build/payperiod/${periodNo}/office/${office}`);
            if (response.ok) {
                window.alert('Finished refreshing billing data.');
                runSearch(periodNo, searchCriteria);
            } else {
                window.alert('Unable to refresh billing data. Please try again.');
            }
        } catch (err) {
            window.alert(err);
        } finally {
            setBuilding(false);
        }
    };

    // Mirrors B_EXPAND/B_COLL (ExpandAllCanvas/CollapseAllCanvas) - same pattern
    // ProjectBillingPage.tsx already uses: one boolean fed to every CollapsibleSection's
    // forceExpanded, flipped by the buttons living in TeNonReconSearch's own main bar.
    const expandAllPanels = () => {
        setExpandAllSections(true);
    };

    const collapseAllPanels = () => {
        setExpandAllSections(false);
    };

    // Mirrors WHEN-BUTTON-PRESSED [oid 478]/XL_NonRecon_TE - guards only on period_no (matching
    // Oracle's own trigger, which doesn't check office/manager filters), downloads straight to the
    // browser rather than Oracle's own file-save-dialog + Download_AS_File/webutil flow (no browser
    // equivalent of a client-side save-location prompt).
    const handleExcelExport = async () => {
        if (!selectedPayPeriod) {
            window.alert('Please select a pay period before exporting.');
            return;
        }

        const periodNo = Number(selectedPayPeriod);
        setExportingExcel(true);
        try {
            await getExcel(`/v1.0/AccountsPayable/timeandexpense/nonrecon/excel/payperiod/${periodNo}`);
            if (excelResponse.ok) {
                const blob = await excelResponse.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.getElementById('nonReconExcelDownload') as HTMLAnchorElement;
                a.href = blobUrl;
                a.download = 'Non-ReconciledTime.xlsx';
                a.click();
                window.URL.revokeObjectURL(blobUrl);
            } else {
                window.alert('Unable to export to Excel. Please try again.');
            }
        } catch (err) {
            window.alert(err);
        } finally {
            setExportingExcel(false);
        }
    };

    // Mirrors Oracle's own master-detail: clicking a Prospects row queries TIME_SHEET_NORECON for
    // just that prospect (Prospect Information, C03).
    const handleSelectProspect = async (prospect: TeNonReconProspect) => {
        if (!selectedPayPeriod || prospect.clNo === undefined || prospect.prNo === undefined) {
            return;
        }
        setSelectedProspect(prospect);
        setLoadingDetail(true);
        let result = await getDetail(`/v1.0/AccountsPayable/timeandexpense/nonrecon/detail/payperiod/${Number(selectedPayPeriod)}/client/${prospect.clNo}/prospect/${prospect.prNo}`);
        setDetailRows(detailResponse.ok && Array.isArray(result) ? result : []);
        setLoadingDetail(false);
    };

    const showDialog = (dialog: DialogList) => dispatch(actions.layout.showDialog(dialog));
    const hideDialog = () => dispatch(actions.layout.hideDialog());

    // Mirrors WHEN-MOUSE-DOUBLECLICK [oid 285] - double-clicking a Project Information row. type
    // null means no record is under the cursor; type 'OI' (Office Expense) can't be edited here at
    // all. Otherwise 'NI' (No Invoice) edits the invoice/bill-rate/bill-date trio, everything else
    // edits the check/pay-rate/pay-date trio - same labels/title Oracle's own copy() calls set.
    const handleEditTimeEntry = async (detail: TeNonReconDetail) => {
        if (detail.tmId === undefined || detail.type === undefined || detail.type === null) {
            window.alert('Record not available..');
            return;
        }
        if (detail.type === 'OI') {
            window.alert('Cannot update Office Expense..');
            return;
        }

        const isInvoice = detail.type === 'NI';
        const info = await getEditInfo(`/v1.0/AccountsPayable/timeandexpense/nonrecon/edit/${detail.tmId}/type/${detail.type}`);
        if (!editResponse.ok) {
            return;
        }

        setEditData({
            tmId: detail.tmId,
            type: detail.type,
            action: isInvoice ? 'Edit Billing' : 'Edit Payment',
            name: `${detail.name ?? ''} on ${formatDateOrBlank(detail.tmDate)}`,
            disp1: isInvoice ? 'Invoice No.' : 'Check No.',
            disp2: isInvoice ? 'Bill Rate' : 'Pay Rate',
            disp3: isInvoice ? 'Date of Invoice' : 'Date of Check',
            docNo: info?.docNo,
            dayRate: info?.dayRate,
            docDate: info?.docDate,
        });
        showDialog(DialogList.EDIT_NONRECON_TIME);
    };

    // Mirrors B_OK [oid 492] - on success, closes the popup and re-queries Time_Sheet_NoRecon
    // (Query_Block('Time_Sheet_NoRecon')), i.e. re-fetches the currently selected prospect's detail.
    const handleSaveTimeEntry = async (tmId: number, type: string, docNo: number | null, dayRate: number | null, docDate: string | null) => {
        const result = await putEditInfo('/v1.0/AccountsPayable/timeandexpense/nonrecon/edit', { tmId, type, docNo, dayRate, docDate });
        if (editResponse.ok) {
            hideDialog();
            if (selectedProspect) {
                handleSelectProspect(selectedProspect);
            }
        } else {
            window.alert(result?.message || 'Unable to update this time record.');
        }
    };

    // Mirrors WHEN-MOUSE-DOUBLECLICK [oid 369] - double-clicking a Modified Time Records row opens
    // its change-history drill-down (TIME_SHEET_CHANGE_LOG rows for this tm_id).
    const handleShowChangeDetail = (changeRecord: TeNonReconChangeRecord) => {
        if (changeRecord.tmId === undefined) {
            window.alert('No record available to check...');
            return;
        }
        setChangeDetailTmId(changeRecord.tmId);
        showDialog(DialogList.NONRECON_CHANGE_DETAIL);
    };

    const prospectsTotals = {
        days: prospects.reduce((sum, p) => sum + (p.days ?? 0), 0),
        services: prospects.reduce((sum, p) => sum + (p.services ?? 0), 0),
        expTotal: prospects.reduce((sum, p) => sum + (p.expTotal ?? 0), 0),
        total: prospects.reduce((sum, p) => sum + (p.total ?? 0), 0),
    };

    const detailTotals = {
        days: detailRows.reduce((sum, d) => sum + (d.days ?? 0), 0),
        services: detailRows.reduce((sum, d) => sum + (d.services ?? 0), 0),
        expTotal: detailRows.reduce((sum, d) => sum + (d.expTotal ?? 0), 0),
        total: detailRows.reduce((sum, d) => sum + (d.total ?? 0), 0),
    };

    const displayProspects = () => (
        <table className="project-billing te-nonrecon-grid" style={{ width: '100%', tableLayout: 'fixed' }}>
            <thead className="project-billing">
                <tr>
                    <th style={{ width: '4%', textAlign: 'left' }}>Type</th>
                    <th style={{ width: '5%', textAlign: 'left', whiteSpace: 'nowrap' }}>Client #</th>
                    <th style={{ width: '5%', textAlign: 'left', whiteSpace: 'nowrap' }}>Project #</th>
                    <th style={{ width: '22%', textAlign: 'left', ...NOWRAP_CLIP }}>Project Name</th>
                    <th style={{ width: '7%', textAlign: 'left' }}>Office</th>
                    <th style={{ width: '8%', textAlign: 'left', whiteSpace: 'nowrap' }}>Manager #</th>
                    <th style={{ width: '18%', textAlign: 'left', ...NOWRAP_CLIP }}>Manager Name</th>
                    <th style={{ width: '8%', textAlign: 'left' }}>Days</th>
                    <th style={{ width: '10%', textAlign: 'right' }}>Services</th>
                    <th style={{ width: '10%', textAlign: 'right' }}>Total</th>
                </tr>
            </thead>
            <tbody className="project-billing">
                {!loadingProspects && prospects.map((p, index) => (
                    <tr
                        key={`${p.clNo}-${p.prNo}-${index}`}
                        className="table-item-hover"
                        style={{
                            // .project-billing's own CSS sets tr:not(thead tr) { line-height: 0 },
                            // meant for the input-per-cell grids elsewhere in this app (the input
                            // gets its own height via padding despite that) - this table uses plain
                            // <td> text instead, so it needs line-height restored per-row (only an
                            // inline style beats that class rule).
                            lineHeight: 'normal',
                            cursor: 'pointer',
                            backgroundColor: selectedProspect && selectedProspect.clNo === p.clNo && selectedProspect.prNo === p.prNo ? '#FFFF66' : '',
                        }}
                        onClick={() => handleSelectProspect(p)}
                    >
                        <td style={{ width: '4%' }}>{p.type}</td>
                        <td style={{ width: '5%' }}>{p.clNo}</td>
                        <td style={{ width: '5%' }}>{p.prNo}</td>
                        <td style={{ width: '22%', ...NOWRAP_CLIP }}>{p.prName}</td>
                        <td style={{ width: '7%' }}>{p.office}</td>
                        <td style={{ width: '8%' }}>{p.tsdMgr}</td>
                        <td style={{ width: '18%', ...NOWRAP_CLIP }}>{p.manager}</td>
                        <td style={{ width: '8%' }}>{(p.days ?? 0).toFixed(4)}</td>
                        <td style={{ width: '10%', textAlign: 'right' }}>{formatCurrency(p.services)}</td>
                        <td style={{ width: '10%', textAlign: 'right' }}>{formatCurrency(p.total)}</td>
                    </tr>
                ))}
                {!loadingProspects && prospects.length === 0 && (
                    <tr style={{ lineHeight: 'normal' }}>
                        <td colSpan={10} className="bold-centered">No Non-Reconciled Projects Found</td>
                    </tr>
                )}
            </tbody>
            <tfoot className="project-billing">
                {/* Explicit borderTop on every cell (not just the row) - .project-billing's own
                    CSS only sets a transparent border on <tr>, which border-collapse can't be
                    relied on across the thead/tbody/tfoot "each its own display:table" hack, so a
                    row-level border alone wouldn't reliably draw. This keeps a visible rule directly
                    above Totals regardless of how many body rows are showing. */}
                <tr style={{ fontWeight: 'bold', lineHeight: 'normal' }}>
                    <td colSpan={7} style={{ width: '69%', borderTop: '2px solid #000' }}>Totals:</td>
                    <td style={{ width: '8%', borderTop: '2px solid #000' }}>{prospectsTotals.days.toFixed(4)}</td>
                    <td style={{ width: '10%', textAlign: 'right', borderTop: '2px solid #000' }}>{formatCurrency(prospectsTotals.services)}</td>
                    <td style={{ width: '10%', textAlign: 'right', borderTop: '2px solid #000' }}>{formatCurrency(prospectsTotals.total)}</td>
                </tr>
            </tfoot>
        </table>
    );

    const displayDetail = () => {
        if (!selectedProspect) {
            return <div className="bold-centered" style={{ padding: '1rem' }}>Select a project above to see its detail.</div>;
        }

        return (
            <table className="project-billing te-nonrecon-grid" style={{ width: '100%', tableLayout: 'fixed' }}>
                <thead className="project-billing">
                    <tr>
                        <th style={{ width: '24%', textAlign: 'left', ...NOWRAP_CLIP }}>Landman</th>
                        <th style={{ width: '7%', textAlign: 'left' }}>Work Date</th>
                        <th style={{ width: '6%', textAlign: 'left' }}>Subcode</th>
                        <th style={{ width: '7%', textAlign: 'left' }}>AltSubcode</th>
                        <th style={{ width: '5%', textAlign: 'left' }}>Acct.</th>
                        <th style={{ width: '4%', textAlign: 'left' }}>Days</th>
                        <th style={{ width: '6%', textAlign: 'right' }}>Services</th>
                        <th style={{ width: '5%', textAlign: 'right' }}>Mileage</th>
                        <th style={{ width: '5%', textAlign: 'right' }}>Motel</th>
                        <th style={{ width: '5%', textAlign: 'right' }}>Meals</th>
                        <th style={{ width: '5%', textAlign: 'right' }}>Notary</th>
                        <th style={{ width: '5%', textAlign: 'right' }}>Copies</th>
                        <th style={{ width: '5%', textAlign: 'right' }}>Phone</th>
                        <th style={{ width: '6%', textAlign: 'right' }}>Total</th>
                    </tr>
                </thead>
                <tbody className="project-billing">
                    {!loadingDetail && detailRows.map((d, index) => (
                        <tr
                            key={`${d.tsdNo}-${d.tmDate}-${index}`}
                            className="table-item-hover"
                            style={{ lineHeight: 'normal', cursor: 'pointer' }}
                            onDoubleClick={() => handleEditTimeEntry(d)}
                        >
                            <td style={{ width: '24%', ...NOWRAP_CLIP }}>{d.name}</td>
                            <td style={{ width: '7%' }}>{formatDateOrBlank(d.tmDate)}</td>
                            <td style={{ width: '6%' }}>{d.scNo}</td>
                            <td style={{ width: '7%' }}>{d.unNo}</td>
                            <td style={{ width: '5%' }}>{d.acctNo}</td>
                            <td style={{ width: '4%' }}>{(d.days ?? 0).toFixed(4)}</td>
                            <td style={{ width: '6%', textAlign: 'right' }}>{formatCurrency(d.services)}</td>
                            <td style={{ width: '5%', textAlign: 'right' }}>{formatCurrency(d.mileage)}</td>
                            <td style={{ width: '5%', textAlign: 'right' }}>{formatCurrency(d.motel)}</td>
                            <td style={{ width: '5%', textAlign: 'right' }}>{formatCurrency(d.meals)}</td>
                            <td style={{ width: '5%', textAlign: 'right' }}>{formatCurrency(d.notary)}</td>
                            <td style={{ width: '5%', textAlign: 'right' }}>{formatCurrency(d.copies)}</td>
                            <td style={{ width: '5%', textAlign: 'right' }}>{formatCurrency(d.phone)}</td>
                            <td style={{ width: '6%', textAlign: 'right' }}>{formatCurrency(d.total)}</td>
                        </tr>
                    ))}
                    {!loadingDetail && detailRows.length === 0 && (
                        <tr style={{ lineHeight: 'normal' }}>
                            <td colSpan={14} className="bold-centered">No Detail Records Found</td>
                        </tr>
                    )}
                </tbody>
                <tfoot className="project-billing">
                    <tr style={{ fontWeight: 'bold', lineHeight: 'normal' }}>
                        <td colSpan={5} style={{ width: '49%' }}>Totals:</td>
                        <td style={{ width: '4%' }}>{detailTotals.days.toFixed(4)}</td>
                        <td style={{ width: '6%', textAlign: 'right' }}>{formatCurrency(detailTotals.services)}</td>
                        <td colSpan={6} style={{ width: '30%' }} />
                        <td style={{ width: '6%', textAlign: 'right' }}>{formatCurrency(detailTotals.total)}</td>
                    </tr>
                </tfoot>
            </table>
        );
    };

    const displayChangeRecords = () => (
        <table className="project-billing te-nonrecon-grid" style={{ width: '100%', tableLayout: 'fixed' }}>
            <thead className="project-billing">
                <tr>
                    <th style={{ width: '22%', textAlign: 'left', ...NOWRAP_CLIP }}>Landman</th>
                    <th style={{ width: '8%', textAlign: 'left', whiteSpace: 'nowrap' }}>Work Date</th>
                    <th style={{ width: '6%', textAlign: 'left' }}>Client #</th>
                    <th style={{ width: '6%', textAlign: 'left' }}>Project #</th>
                    <th style={{ width: '7%', textAlign: 'right' }}>Hours</th>
                    <th style={{ width: '9%', textAlign: 'left' }}>Check #</th>
                    <th style={{ width: '9%', textAlign: 'right' }}>Pay Rate</th>
                    <th style={{ width: '10%', textAlign: 'left' }}>Invoice #</th>
                    <th style={{ width: '10%', textAlign: 'right' }}>Bill Rate</th>
                </tr>
            </thead>
            <tbody className="project-billing">
                {!loadingChangeRecords && changeRecords.map((c, index) => (
                    <tr
                        key={`${c.tsdNo}-${c.tmDate}-${index}`}
                        className="table-item-hover"
                        style={{ lineHeight: 'normal', cursor: 'pointer' }}
                        onDoubleClick={() => handleShowChangeDetail(c)}
                    >
                        <td style={{ width: '22%', ...NOWRAP_CLIP }}>{c.akaName}</td>
                        <td style={{ width: '8%', whiteSpace: 'nowrap' }}>{formatDateOrBlank(c.tmDate)}</td>
                        <td style={{ width: '6%' }}>{c.clNo}</td>
                        <td style={{ width: '6%' }}>{c.prNo}</td>
                        <td style={{ width: '7%', textAlign: 'right' }}>{(c.hours ?? 0).toFixed(2)}</td>
                        <td style={{ width: '9%' }}>{c.checkNo}</td>
                        <td style={{ width: '9%', textAlign: 'right' }}>{formatCurrency(c.payRate)}</td>
                        <td style={{ width: '10%' }}>{c.invoiceNo}</td>
                        <td style={{ width: '10%', textAlign: 'right' }}>{formatCurrency(c.billRate)}</td>
                    </tr>
                ))}
                {!loadingChangeRecords && changeRecords.length === 0 && (
                    <tr style={{ lineHeight: 'normal' }}>
                        <td colSpan={9} className="bold-centered">No Modified Time Records Found</td>
                    </tr>
                )}
            </tbody>
        </table>
    );

    return (
        <div>
            <PBNavBar />
            <TeNonReconSearch
                selectedPayperiod={selectedPayPeriod}
                setSelectedPayPeriod={setSelectedPayPeriod}
                setPayPeriodSearchTerm={setPayPeriodSearchTerm}
                payPeriods={(payPeriods ?? []).map((pp) => ({ id: pp.id, name: pp.name ?? '' }))}
                onSearch={handleSearch}
                onExpandAll={expandAllPanels}
                onCollapseAll={collapseAllPanels}
            />
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <div style={{ width: 'fit-content', margin: '0 auto' }}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel>
                                <div className="form-row" style={{ textAlign: 'center', verticalAlign: 'middle', justifyContent: 'center', margin: 0, padding: 0, flexWrap: 'nowrap' }}>
                                    <Button
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', width: '10rem', height: '3rem' }}
                                        text={building ? 'Refreshing...' : 'Refresh Data'}
                                        disabled={building}
                                        loading={building}
                                        onClick={handleBuild}
                                    />
                                    <Button
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', width: '10rem', height: '3rem', marginLeft: '1rem' }}
                                        text={exportingExcel ? 'Exporting...' : 'Excel Export'}
                                        disabled={exportingExcel}
                                        loading={exportingExcel}
                                        onClick={handleExcelExport}
                                    />
                                </div>
                            </Panel>
                        </Panel>
                        <a style={{ display: 'none' }} id="nonReconExcelDownload" />
                    </div>
                </Page.Section>
            </Page>
            <Page style={{ justifyContent: 'center' }}>
                <Page.Section width={12}>
                    <div style={{ maxWidth: '130rem', margin: '0 auto' }}>
                        <CollapsibleSection title="Projects - Non-Reconciled" forceExpanded={expandAllSections}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle="Projects - Non-Reconciled">
                                    <div style={{ border: 'solid rgb(99, 147, 193) 1px' }}>
                                        {loadingProspects ? (
                                            <div style={{ textAlign: 'center', padding: '2rem' }}><LoadingSpinner /></div>
                                        ) : displayProspects()}
                                    </div>
                                </Panel>
                            </Panel>
                        </CollapsibleSection>
                        <CollapsibleSection title="Project Information" forceExpanded={expandAllSections}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle="Project Information">
                                    <div style={{ border: 'solid rgb(99, 147, 193) 1px' }}>
                                        {loadingDetail ? (
                                            <div style={{ textAlign: 'center', padding: '2rem' }}><LoadingSpinner /></div>
                                        ) : displayDetail()}
                                    </div>
                                </Panel>
                            </Panel>
                        </CollapsibleSection>
                        <CollapsibleSection title="Modified Time Records" forceExpanded={expandAllSections}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle="Modified Time Records">
                                    <div style={{ border: 'solid rgb(99, 147, 193) 1px' }}>
                                        {loadingChangeRecords ? (
                                            <div style={{ textAlign: 'center', padding: '2rem' }}><LoadingSpinner /></div>
                                        ) : displayChangeRecords()}
                                    </div>
                                </Panel>
                            </Panel>
                        </CollapsibleSection>
                    </div>
                </Page.Section>
            </Page>
            <EditTimeDialog
                dialogName={DialogList.EDIT_NONRECON_TIME}
                hideDialog={hideDialog}
                data={editData}
                onSave={handleSaveTimeEntry}
            />
            <ChangeDetailDialog
                dialogName={DialogList.NONRECON_CHANGE_DETAIL}
                hideDialog={hideDialog}
                tmId={changeDetailTmId}
            />
        </div>
    );
};

export default TeNonReconPage;



// WEBPACK FOOTER //
// ./src/components/project-billing/pages/TeNonReconPage.tsx